// Player.js
import React, { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { GAME_CONFIG } from './GameConfig';
import { FlashGhost } from './GameAssets';

// speedMult prop'unu buraya ekledik (varsayılan 1.0)
export const Player = React.forwardRef(({ targetPosition, flashTrigger, wTrigger, selectedChamp, isGameOver, isPaused, isZhonya, wMechanicsRef, speedMult = 1.0 }, ref) => {
  const mesh = useRef();
  const materialRef = useRef();
  const position = useRef(new THREE.Vector3(0, 0.5, 0));
  const activeTarget = useRef(new THREE.Vector3(0, 0.5, 0));
  const [ghost, setGhost] = useState({ pos: new THREE.Vector3(), visible: false });
  
  const isDashing = useRef(false);
  const dashTarget = useRef(new THREE.Vector3());

  const getChampColor = () => {
      if (selectedChamp === 'leblanc') return '#9d00ff'; 
      if (selectedChamp === 'zed') return '#111';
      if (selectedChamp === 'yasuo') return '#0077ff';
      return '#00d9ff';
  };

  useEffect(() => {
      if (!isZhonya) {
        activeTarget.current.copy(targetPosition);
      }
  }, [targetPosition, isZhonya]);

  useFrame((state, delta) => {
    if (isGameOver || isPaused || !mesh.current) return;

    if (isZhonya) {
        materialRef.current.color.set("#ffd700");
        mesh.current.position.copy(position.current);
        if (ref && ref.current) ref.current.copy(position.current);
        return; 
    }

    // --- PLAYER MOVEMENT ---
    if (wTrigger && wTrigger.active) {
        if (selectedChamp === 'leblanc') {
            if (!isDashing.current && wTrigger.mode === 'dash') {
                isDashing.current = true;
                const diff = new THREE.Vector3().subVectors(wTrigger.target, position.current);
                const travelDist = Math.min(diff.length(), GAME_CONFIG.W_DASH_DISTANCE);
                if (travelDist > 0.1) diff.normalize();
                dashTarget.current.copy(position.current).add(diff.multiplyScalar(travelDist));
                // Clamp
                const limit = GAME_CONFIG.MAP_SIZE / 2 - 1;
                dashTarget.current.x = THREE.MathUtils.clamp(dashTarget.current.x, -limit, limit);
                dashTarget.current.z = THREE.MathUtils.clamp(dashTarget.current.z, -limit, limit);
                activeTarget.current.copy(dashTarget.current);
            }
            if (wTrigger.mode === 'return') {
                position.current.copy(wTrigger.target);
                isDashing.current = false;
                wTrigger.active = false;
                activeTarget.current.copy(position.current);
            }
        } 
        else if (selectedChamp === 'zed') {
             if (wTrigger.mode === 'teleport') {
                 position.current.copy(wMechanicsRef.current.shadowPos); 
                 wTrigger.active = false;
                 activeTarget.current.copy(position.current);
             }
        }
        else if (selectedChamp === 'yasuo') {
            wTrigger.active = false; 
        }
    }

    // Movement Physics
    if (isDashing.current) {
        // Dash hızı Ghost'tan etkilenmez, sabit kalır
        const dir = new THREE.Vector3().subVectors(dashTarget.current, position.current);
        if (dir.length() > 0.5) {
            dir.normalize();
            position.current.add(dir.multiplyScalar(GAME_CONFIG.W_DASH_SPEED * delta));
        } else {
            position.current.copy(dashTarget.current);
            isDashing.current = false;
            if (wTrigger && selectedChamp === 'leblanc') wTrigger.active = false;
        }
    } else {
        // NORMAL YÜRÜME (Hız Çarpanı Burada Devreye Giriyor)
        const direction = new THREE.Vector3().subVectors(activeTarget.current, position.current);
        const distance = direction.length();
        
        if (distance > 0.1) {
            direction.normalize();
            
            // GÜNCELLEME BURADA: Hızı speedMult ile çarpıyoruz
            const currentSpeed = GAME_CONFIG.PLAYER_SPEED * speedMult;
            
            const moveStep = Math.min(distance, currentSpeed * delta);
            position.current.add(direction.multiplyScalar(moveStep));
        }
    }

    // Flash Logic
    if (flashTrigger && flashTrigger.active) {
      setGhost({ pos: position.current.clone(), visible: true });
      const diff = new THREE.Vector3().subVectors(flashTrigger.target, position.current);
      const travelDist = Math.min(diff.length(), GAME_CONFIG.FLASH_DISTANCE);
      if (travelDist > 0.1) diff.normalize();
      position.current.add(diff.multiplyScalar(travelDist));
      const limit = GAME_CONFIG.MAP_SIZE / 2 - 1; 
      position.current.x = THREE.MathUtils.clamp(position.current.x, -limit, limit);
      position.current.z = THREE.MathUtils.clamp(position.current.z, -limit, limit);
      activeTarget.current.copy(position.current);
      isDashing.current = false; 
      flashTrigger.active = false;
    }

    mesh.current.position.copy(position.current);
    if (materialRef.current) materialRef.current.color.set(getChampColor());
    
    // Rotation
    const targetToLook = isDashing.current ? dashTarget.current : activeTarget.current;
    if (position.current.distanceTo(targetToLook) > 0.2) {
        mesh.current.lookAt(targetToLook.x, 0.5, targetToLook.z);
    }
    
    if (ref && ref.current) ref.current.copy(position.current);
  });

  return (
    <>
        <group ref={mesh} position={[0, 0.5, 0]}>
            <mesh castShadow>
                <capsuleGeometry args={[GAME_CONFIG.PLAYER_RADIUS, 1.8, 4, 8]} />
                <meshStandardMaterial ref={materialRef} color={getChampColor()} emissive="#000" />
            </mesh>
            <mesh position={[0, 0.6, 0.4]}>
                 <boxGeometry args={[0.6, 0.2, 0.3]} />
                 <meshStandardMaterial color="#fff" />
            </mesh>
        </group>
        <FlashGhost position={ghost.pos} visible={ghost.visible} onComplete={() => setGhost({ ...ghost, visible: false })} />
    </>
  );
});